import { useCallback, useEffect } from 'react';
import { useBlocker } from 'react-router';

export interface ExitGuard {
  blocked: boolean;
  /** Остаться: перехват снимается, адрес не меняется. */
  cancel: () => void;
  /** Уйти: отложенный переход доводится до конца. */
  confirm: () => void;
}

/** Перехват ухода с экрана, пока `active`.
 *
 *  Ловит крестик, кнопку «назад» и любую ссылку одним правилом вместо трёх
 *  обработчиков. Чего он НЕ ловит: перезагрузку, pull-to-refresh и закрытие
 *  вкладки (раздел 8.1). Для них ниже `beforeunload`, ненадёжный на
 *  мобильных. Незавершённая сессия и так не сохраняется, поэтому цена
 *  промаха — потеря текущей тренировки; диалог остаётся защитой от
 *  случайного нажатия, а не гарантией.
 *
 *  `active` обязан гаснуть, когда уходить уже можно. Иначе переход на итоги
 *  после последнего задания сам упрётся в этот же перехват, и ребёнок
 *  застрянет на пустом экране тренировки. */
export function useExitGuard(active: boolean): ExitGuard {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      active && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!active) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [active]);

  /** Ссылки стабильны между перерисовками. Иначе эффект в диалоге, который
   *  зависит от `onCancel`, снимается и ставится заново на каждую
   *  перерисовку родителя: при снятии он возвращает фокус — на кнопку под
   *  подложкой, — а при установке уводит его обратно на «Отмену». Ребёнок,
   *  дошедший табом до «Выйти и потерять», просто теряет место. */
  const cancel = useCallback(() => blocker.reset?.(), [blocker]);
  const confirm = useCallback(() => blocker.proceed?.(), [blocker]);

  return { blocked: blocker.state === 'blocked', cancel, confirm };
}
